import _ from "lodash";
import { useContext, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import type { AccountHoldingResponse } from "../../../api/src/accounts/accounts.service";
import type { HouseholdsController } from "../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import HoldingsByAccountTable, {
  HoldingsFieldMode,
} from "../components/HoldingsByAccountTable";
import { deserializeModelSummaryPercents } from "../clients/percents";
import { useFirmRiskProfiles } from "../firm/lib";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import { useQuerySecurities } from "../lib/security";
import { RebalanceContext } from "./RebalanceInfo";
import { useQueryHouseholdHoldings } from "../clients/household/lib";

type HoldingsMode = "current" | "target" | "post-rebalance";

/**
 * The realized keys of an `AccountHoldingResponse` built here from a
 * post-rebalance holding or a model weight, which has no realized gain.
 */
const NO_REALIZED_GAINS: Pick<
  AccountHoldingResponse,
  "realizedGainLoss" | "realizedGainLossST" | "realizedGainLossLT"
> = {
  realizedGainLoss: 0,
  realizedGainLossST: 0,
  realizedGainLossLT: 0,
};

/**
 * The keys of an `AccountHoldingResponse` that only a position on the end
 * date supplies, each `null` (APP-1653; `NO_POSITION_KEYS`,
 * `api/src/accounts/accounts.service.ts`): what one built here from a model
 * weight, or from a security the account buys and did not hold before, has
 * for them. The holdings table reads none of them.
 */
const NO_POSITION_KEYS: Pick<
  AccountHoldingResponse,
  | "id"
  | "bridgeftId"
  | "asOfDate"
  | "direction"
  | "firstOpenDate"
  | "costBasisKnown"
  | "unrealizedGainLossLT"
  | "unrealizedGainLossST"
  | "unrealizedGain"
  | "unrealizedGainLT"
  | "unrealizedGainST"
  | "unrealizedLoss"
  | "unrealizedLossLT"
  | "unrealizedLossST"
> = {
  id: null,
  bridgeftId: null,
  asOfDate: null,
  direction: null,
  firstOpenDate: null,
  costBasisKnown: null,
  unrealizedGainLossLT: null,
  unrealizedGainLossST: null,
  unrealizedGain: null,
  unrealizedGainLT: null,
  unrealizedGainST: null,
  unrealizedLoss: null,
  unrealizedLossLT: null,
  unrealizedLossST: null,
};

/**
 * `record` without the keys whose value is `null`. A `postRebalanceHoldings`
 * entry carries every key, `null` where a security the account buys and did
 * not hold before has no value (`PostRebalanceHolding`,
 * `api/src/rebalances/rebalances.service.ts`). The `security` of an
 * `AccountHoldingResponse` declares a value for keys that entry's `security`
 * has as `null`, so a `null` key is left out and the holdings table reads it
 * as it always has.
 */
export function presentKeys<TRecord extends object>(
  record: TRecord,
): { [Key in keyof TRecord]?: NonNullable<TRecord[Key]> } {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== null),
  ) as { [Key in keyof TRecord]?: NonNullable<TRecord[Key]> };
}

/**
 * Remounts the screen per household, so the model summaries the page walk
 * collected for one household are never shown under another
 * (`lib/pagination.ts`).
 */
const RebalanceHoldings = () => {
  const { household } = useContext(RebalanceContext);

  return <RebalanceHoldingsOfHousehold key={household?.id} />;
};

const RebalanceHoldingsOfHousehold = () => {
  const { household, rebalance } = useContext(RebalanceContext);

  const [holdingsMode, setHoldingsMode] = useState<HoldingsMode>("current");
  const [fieldMode, setFieldMode] = useState<HoldingsFieldMode>("value");

  const { data: dataHoldings, isPending: isPendingHoldings } =
    useQueryHouseholdHoldings(household?.id ?? -1, {
      enabled: typeof household !== "undefined",
    });

  // `GET /v1/households/:id/model-summary` answers a page, in account ID
  // order (APP-1653), and the table needs the model weights of every account,
  // so the walk keeps asking until it holds every one. Each weight comes on 0
  // to 100 and is read on 0 to 1 (`deserializeModelSummaryPercents`).
  const {
    rows: modelSummaryRows,
    hasEveryRow: hasEveryModelSummary,
    isError: isErrorModelSummaries,
  } = useAuthenticatedFetchAllPages<
    UnpackResponse<
      HouseholdsController["getHouseholdModelSummary"]
    >["data"][number]
  >(
    `/v1/households/${household?.id}/model-summary`,
    {},
    { enabled: typeof household !== "undefined" },
  );
  const modelSummaries = useMemo(
    () =>
      hasEveryModelSummary
        ? modelSummaryRows.map((summary) =>
            deserializeModelSummaryPercents(summary),
          )
        : [],
    [hasEveryModelSummary, modelSummaryRows],
  );

  // `/v1/firm/risk` answers a PAGE, not the whole collection (APP-1563).
  // `useFirmRiskProfiles` walks it; reading one response as the whole set would
  // drop every holding allocated only to a risk level past the first page, and
  // `tsc` cannot see that — a page and the whole array have the same element
  // type. `RebalanceHoldings.pagination.spec.tsx` is what holds it.
  const { riskProfiles } = useFirmRiskProfiles();

  // The route returns them in display_order (`firms.service.ts`,
  // `getFirmRiskProfilesPage`), and the walk concatenates the pages in the
  // order it asked for them, so the sequence survives.
  const riskCategories = useMemo(
    () => riskProfiles.map((profile) => profile.name),
    [riskProfiles],
  );

  const modelSecurities = modelSummaries.flatMap((account) =>
    Object.keys(account.model),
  );

  const { data: securities, isPending: isPendingRebalanceSecurities } =
    useQuerySecurities(
      _.uniq([
        ...(rebalance?.postRebalanceHoldings ?? []).map(
          (holding) => holding.security?.identifier ?? "",
        ),
        ...modelSecurities,
      ]).filter((symbol) => symbol !== ""),
      { enabled: typeof rebalance !== "undefined" },
    );

  const securitiesMap = useMemo(
    () => _.keyBy(securities ?? [], "identifier"),
    [securities],
  );

  const holdings: AccountHoldingResponse[] = useMemo(
    () =>
      holdingsMode === "current"
        ? (dataHoldings ?? []).flatMap((account) => account.holdings)
        : holdingsMode === "post-rebalance" && typeof securities !== "undefined"
          ? (rebalance?.accounts ?? []).flatMap((account) =>
              (account.postRebalanceHoldings ?? []).map(
                (holding): AccountHoldingResponse => {
                  const security =
                    securitiesMap[holding.security?.identifier ?? ""];

                  return {
                    // `AccountHoldingResponse` requires the three realized
                    // keys and the keys only a position supplies
                    // (APP-1653); a key the holding carries wins.
                    ...NO_POSITION_KEYS,
                    ...NO_REALIZED_GAINS,
                    ...presentKeys(holding),
                    units: holding.units,
                    appraisedUnitPrice: holding.appraisedUnitPrice,
                    value: holding.value,
                    accountId: account.id,
                    securityId: holding.securityId,
                    security: security ?? {
                      ...presentKeys(holding.security ?? {}),
                      assetClass: "other",
                      riskAllocation: {
                        unknown: 1,
                      },
                    },
                    income: 0,
                    unrealizedGainLoss: 0,
                  };
                },
              ),
            )
          : modelSummaries.flatMap((account) => {
              return (Object.entries(account.model) as [string, number][]).map(
                ([symbol, weight]): AccountHoldingResponse => {
                  const security = securitiesMap[symbol];

                  return {
                    ...NO_POSITION_KEYS,
                    ...NO_REALIZED_GAINS,
                    accountId: account.accountId,
                    securityId: security.id,
                    security: security ?? {
                      assetClass: "other",
                      riskAllocation: {
                        unknown: 1,
                      },
                    },
                    appraisedUnitPrice: 0,
                    units: 0,
                    value: weight * (household?.householdBalance ?? 0),
                    income: 0,
                    unrealizedGainLoss: 0,
                  };
                },
              );
            }),
    [
      dataHoldings,
      modelSummaries,
      securities,
      holdingsMode,
      household?.householdBalance,
      rebalance?.accounts,
      securitiesMap,
    ],
  );

  return (
    <>
      <Row>
        <Col xxl={2} md={4} className="mb-3">
          <Form.Select
            value={holdingsMode}
            onChange={(ev) =>
              setHoldingsMode(ev.currentTarget.value as HoldingsMode)
            }
          >
            <option value="current">Current</option>
            <option value="target">Target</option>
            <option value="post-rebalance">Post-Rebalance</option>
          </Form.Select>
        </Col>
        <Col xxl={3} xl={4} md={6} className="mb-3">
          <Form.Select
            value={fieldMode}
            onChange={(ev) =>
              setFieldMode(ev.currentTarget.value as HoldingsFieldMode)
            }
          >
            <option value="value">Value</option>
            <option value="weight">Weight</option>
            <option value="modelWeight">Model Weight</option>
            <option value="modelWeightDifference">
              Current - Model Weight
            </option>
          </Form.Select>
        </Col>
      </Row>
      <Row>
        <Col>
          <HoldingsByAccountTable
            holdings={holdings}
            accounts={household?.accounts ?? []}
            transactions={[]}
            modelSummaries={modelSummaries}
            fieldMode={fieldMode}
            isLoading={
              isPendingHoldings ||
              (!hasEveryModelSummary && !isErrorModelSummaries) ||
              isPendingRebalanceSecurities
            }
            riskCategories={riskCategories}
          />
        </Col>
      </Row>
    </>
  );
};

export default RebalanceHoldings;
